import React, { useState } from "react";
import { useContent } from "@/context/ContentContext";
import api from "@/lib/api";
import Icon from "@/components/Icon";

export default function Contact() {
  const { content, loading } = useContent();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    topic: "Schedule a Consultation",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  if (loading || !content) return null;
  const c = content.contact || {};

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      await api.post("/contact", form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", phone: "", company: "", topic: c.topics?.[1] || "Schedule a Consultation", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d) ? d.map((e) => e.msg || "").filter(Boolean).join(" ") || "Please check your details."
        : "Something went wrong. Please try again.";
      setStatus({ state: "error", error: msg });
    }
  };

  const topics = c.topics?.length ? c.topics : ["General Enquiry", "Schedule a Consultation"];

  return (
    <div data-testid="page-contact">
      <section className="sp-hero">
        <div className="sp-container py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            {c.eyebrow && <span className="sp-eyebrow">{c.eyebrow}</span>}
            <h1 className="mt-3 text-4xl lg:text-6xl font-extrabold leading-tight">
              {c.title}
              <br />
              <span className="text-gold">{c.title_accent}</span>
            </h1>
            {c.description && (
              <p className="mt-5 max-w-xl text-slate-600 text-lg leading-relaxed">
                {c.description}
              </p>
            )}

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <a
                href={`tel:${(c.phone_dial || c.phone || "").replace(/\s+/g, "")}`}
                className="sp-card flex items-start gap-3"
                data-testid="contact-call"
              >
                <div className="w-11 h-11 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
                  <Icon name="Phone" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Call / WhatsApp</div>
                  <div className="text-navy font-extrabold">{c.phone}</div>
                </div>
              </a>
              <a href={`mailto:${c.email}`} className="sp-card flex items-start gap-3" data-testid="contact-email">
                <div className="w-11 h-11 rounded-full bg-navy/10 text-navy flex items-center justify-center shrink-0">
                  <Icon name="Mail" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</div>
                  <div className="text-navy font-extrabold break-all">{c.email}</div>
                </div>
              </a>
              {c.whatsapp_url && (
                <a
                  href={c.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sp-card flex items-start gap-3"
                  data-testid="contact-whatsapp"
                >
                  <div className="w-11 h-11 rounded-full bg-green-500/15 text-green-600 flex items-center justify-center shrink-0">
                    <Icon name="MessageSquare" size={20} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">WhatsApp</div>
                    <div className="text-navy font-extrabold">Chat with us</div>
                  </div>
                </a>
              )}
              <div className="sp-card flex items-start gap-3" data-testid="contact-location">
                <div className="w-11 h-11 rounded-full bg-orange-500/15 text-orange-600 flex items-center justify-center shrink-0">
                  <Icon name="MapPin" size={20} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">We Serve</div>
                  <div className="text-navy font-extrabold">{c.audience}</div>
                </div>
              </div>
            </div>

            {c.team_image && (
              <div className="mt-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white max-w-xl">
                <img src={c.team_image} alt="African professionals collaborating" className="w-full h-auto object-cover" loading="lazy" />
              </div>
            )}
          </div>

          {/* FORM */}
          <div className="rounded-3xl bg-navy text-white p-7 lg:p-9 shadow-2xl" data-testid="contact-form-card">
            <div className="flex items-center gap-3 mb-1">
              <Icon name="Calendar" size={22} className="text-gold" />
              <h2 className="text-2xl font-extrabold text-white">{c.form_title}</h2>
            </div>
            <p className="text-white/80 text-sm mb-6">{c.form_subtitle}</p>

            {status.state === "success" ? (
              <div className="bg-white/10 border border-white/20 rounded-2xl p-6 text-center" data-testid="contact-success">
                <Icon name="CheckCircle2" size={36} className="text-gold mx-auto" />
                <h3 className="mt-3 text-xl font-extrabold text-white">Thank you!</h3>
                <p className="text-white/80 mt-2">
                  We've received your message and will get back to you shortly.
                </p>
                <button onClick={() => setStatus({ state: "idle", error: "" })} className="mt-5 sp-btn-gold" data-testid="contact-send-another">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" name="name" value={form.name} onChange={onChange} required testid="contact-input-name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-input-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} testid="contact-input-phone" />
                  <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} testid="contact-input-company" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5">Topic</label>
                  <select
                    name="topic"
                    value={form.topic}
                    onChange={onChange}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold"
                    data-testid="contact-input-topic"
                  >
                    {topics.map((t) => (
                      <option key={t} value={t} className="text-navy">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5">Your message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    required
                    minLength={5}
                    placeholder="Tell us about your project, goals or questions…"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-gold"
                    data-testid="contact-input-message"
                  />
                </div>

                {status.state === "error" && (
                  <div className="text-sm text-red-300 bg-red-500/10 border border-red-400/30 rounded-lg px-3 py-2" data-testid="contact-error">
                    {status.error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  className="sp-btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {status.state === "loading" ? "Sending…" : (<><Icon name="Send" size={18} /> Schedule a Consultation</>)}
                </button>
                <p className="text-[11px] text-white/50 text-center">
                  By submitting, you agree to be contacted by SiliconPay regarding your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      {c.reasons?.length > 0 && (
        <section className="py-20">
          <div className="sp-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="sp-eyebrow">Why Work With Us</span>
              <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold">
                Future-ready. <span className="text-gold">Outcome-obsessed.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.reasons.map((r, i) => (
                <div key={r.title + i} className="sp-card" data-testid={`reason-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-navy text-gold flex items-center justify-center">
                    <Icon name={r.icon} size={22} />
                  </div>
                  <h3 className="mt-4 text-base font-extrabold text-navy">{r.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="sp-strip">
        <div className="sp-container py-16 text-center">
          <Icon name="Lightbulb" size={36} className="text-gold mx-auto" />
          <h3 className="mt-4 text-2xl lg:text-3xl font-extrabold text-white">
            Let's Build the <span className="text-gold">Future</span> Together.
          </h3>
          {c.closing_quote && (
            <p className="mt-3 max-w-2xl mx-auto text-white/85 italic">"{c.closing_quote}"</p>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5">
        {label}{required && <span className="text-gold">*</span>}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-gold"
        data-testid={testid}
      />
    </div>
  );
}
